import { FiRrCrossSmall } from '@app/assets';
import { Button, Typography } from '@app/components';
import { Routes } from '@app/constants/routes';
import { useActivePlan } from '@app/domain/plan/hooks/useActivePlan';
import { EOnboardingStage, ETrialPhase, useTrialPhasesConfig } from '@app/domain/plan/hooks/useTrialPhasesConfig';
import { useUpdateCustomer } from '@app/domain/user/api/user.hooks.api';
import { TranslationKey } from '@app/locales/defaultTranslation';
import { TypedLink } from '@app/router';
import { clsxm } from '@app/styles/clsxm';
import { LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ETrialOnboardingType, useTrialOnboardingDialog } from './TrialOnboardingDialog';

type Props = {
  className?: string;
};

export const OnboardingBanner: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isTrial, daysToExpire } = useActivePlan();
  const { mutate: updateCustomer, isLoading } = useUpdateCustomer();
  const { onboardingStageStep, currentTrialPhase } = useTrialPhasesConfig();
  const { openTrialOnboardingDialog } = useTrialOnboardingDialog(null);

  if (currentTrialPhase === ETrialPhase.ACTIVATION_FOCUS) {
    const content = getOnboardingContent(onboardingStageStep);
    if (!content) {
      return null;
    }

    const onboardingProgress = () => {
      return (
        <div className="flex gap-2 flex-col items-start w-full md:w-auto">
          <div className="flex gap-1 w-full">
            <Typography className="text-xxs">
              {t('subscription.globalBanner.trialProgressSteps', {
                activeStep: getOnboardingStep(onboardingStageStep),
                totalSteps: 3,
              })}
              :
            </Typography>
            <Typography className="text-xxs">{t(content.progressMessage)}</Typography>
          </div>
          <div className="flex gap-1 w-full">
            <LinearProgress
              className={'bg-[#C8D8E9] w-full md:w-16'}
              classes={{
                root: 'rounded h-[4px]',
                bar: 'rounded bg-[#2D5188]',
              }}
              variant="determinate"
              value={100}
            />
            <LinearProgress
              className={'bg-[#C8D8E9] w-full md:w-16'}
              classes={{
                root: 'rounded h-[4px]',
                bar: 'rounded bg-[#2D5188]',
              }}
              variant="determinate"
              value={getOnboardingStep(onboardingStageStep) >= 2 ? 100 : 0}
            />
            <LinearProgress
              className={'bg-[#C8D8E9] w-full md:w-16'}
              classes={{
                root: 'rounded h-[4px]',
                bar: 'rounded bg-[#2D5188]',
              }}
              variant="determinate"
              value={getOnboardingStep(onboardingStageStep) >= 3 ? 100 : 0}
            />
          </div>
        </div>
      );
    };

    return (
      <div
        className={clsxm('bg-[#DFE6ED] px-6 py-5 pr-12 md:pr-6 flex gap-5 items-center relative flex-wrap', className)}
      >
        {onboardingProgress()}
        <div className="inline-flex gap-3 flex-wrap">
          <Typography variant="s" className="font-semibold">
            {t(content.titleKey)}
          </Typography>
          <Typography variant="s">{t(content.messageKey)}</Typography>
          {onboardingStageStep !== EOnboardingStage.COMPLETE && (
            <Typography
              className="text-s underline cursor-pointer"
              {...(onboardingStageStep === EOnboardingStage.NO_QR && {
                onClick: () => {
                  navigate(Routes.customer.create_qr_code);
                },
              })}
              {...(onboardingStageStep === EOnboardingStage.QR_NO_SCAN && {
                onClick: () => {
                  openTrialOnboardingDialog(ETrialOnboardingType.TRIAL_ONBOADRDING);
                },
              })}
            >
              {t(content.actionKey)}
            </Typography>
          )}
        </div>
        {isTrial && (
          <button
            data-testid="top-banner-close-button"
            disabled={isLoading}
            className="absolute cursor-pointer top-3 right-3 disabled:cursor-not-allowed hover:bg-white rounded"
            onClick={() => {
              updateCustomer({
                upsellTopBannerHiddenDate: new Date().toJSON(),
              });
            }}
          >
            <FiRrCrossSmall />
          </button>
        )}
      </div>
    );
  }
  if (currentTrialPhase === ETrialPhase.URGENCY_BEGINS) {
    return (
      <div
        className={clsxm(
          'bg-[#DFE6ED] px-6 py-5 pr-12 md:pr-6 justify-center flex gap-6 md:gap-12 relative',
          className
        )}
      >
        <div className="inline-flex gap-3 flex-wrap">
          <Typography variant="s" className="font-semibold">
            {t('subscription.globalBanner.trialUrgencyBegins.title', {
              daysToExpire,
            })}
          </Typography>
          <Typography variant="s">{t('subscription.globalBanner.trialUrgencyBegins.message')}</Typography>
          <TypedLink className="text-s underline" to={Routes.customer.account.subscription}>
            {t('subscription.globalBanner.trialUrgencyBegins.action')}
          </TypedLink>
        </div>
        {isTrial && (
          <button
            data-testid="top-banner-close-button"
            disabled={isLoading}
            className="absolute cursor-pointer top-3 right-3 disabled:cursor-not-allowed hover:bg-white rounded"
            onClick={() => {
              updateCustomer({
                upsellTopBannerHiddenDate: new Date().toJSON(),
              });
            }}
          >
            <FiRrCrossSmall />
          </button>
        )}
      </div>
    );
  }
  if (currentTrialPhase === ETrialPhase.FINAL_PUSH) {
    return (
      <div
        className={clsxm('bg-red-100 px-6 py-5 pr-12 md:pr-6 justify-center flex gap-6 md:gap-12 relative', className)}
      >
        <div className="inline-flex gap-3 flex-wrap">
          <Typography variant="s" className="font-semibold">
            {t('subscription.globalBanner.trialFinalPush.title')}
          </Typography>
          <Typography variant="s">
            {t('subscription.globalBanner.trialFinalPush.message', {
              daysToExpire,
            })}
          </Typography>
          <TypedLink className="text-s underline" to={Routes.customer.account.subscription}>
            {t('subscription.globalBanner.trialFinalPush.action')}
          </TypedLink>
        </div>
        {isTrial && (
          <button
            data-testid="top-banner-close-button"
            disabled={isLoading}
            className="absolute cursor-pointer top-3 right-3 disabled:cursor-not-allowed hover:bg-white rounded"
            onClick={() => {
              updateCustomer({
                upsellTopBannerHiddenDate: new Date().toJSON(),
              });
            }}
          >
            <FiRrCrossSmall />
          </button>
        )}
      </div>
    );
  }
  if (currentTrialPhase === ETrialPhase.EXPIRED) {
    return (
      <div
        className={clsxm('bg-red-100 px-6 py-6 pr-12 md:pr-6 flex gap-6 md:gap-12 justify-center relative', className)}
      >
        <div className="flex gap-3 flex-wrap justify-start md:justify-center items-center">
          <Typography variant="s" className="font-semibold">
            {t('subscription.globalBanner.trialExpired.title')}
          </Typography>
          <Typography variant="s">{t('subscription.globalBanner.trialExpired.message')}</Typography>
          <Button
            className="text-s bg-transparent border-gray-400 text-gray-700"
            variant="outlined"
            size="small"
            onClick={() => navigate(Routes.customer.account.subscription)}
          >
            {t('trial.upgradeNow')}
          </Button>
        </div>
        {isTrial && (
          <button
            data-testid="top-banner-close-button"
            disabled={isLoading}
            className="absolute cursor-pointer top-3 right-3 disabled:cursor-not-allowed hover:bg-white rounded"
            onClick={() => {
              updateCustomer({
                upsellTopBannerHiddenDate: new Date().toJSON(),
              });
            }}
          >
            <FiRrCrossSmall />
          </button>
        )}
      </div>
    );
  }

  return null;
};

export const getOnboardingContent = (
  stage: EOnboardingStage
): {
  titleKey: TranslationKey;
  messageKey: TranslationKey;
  actionKey: TranslationKey;
  progressMessage: TranslationKey;
} | null => {
  switch (stage) {
    case EOnboardingStage.NO_QR:
      return {
        titleKey: 'subscription.globalBanner.trialFirstQRCode.title',
        messageKey: 'subscription.globalBanner.trialFirstQRCode.message',
        actionKey: 'subscription.globalBanner.trialFirstQRCode.action',
        progressMessage: 'subscription.globalBanner.trialFirstQRCode.progressMessage',
      };
    case EOnboardingStage.QR_NO_SCAN:
      return {
        titleKey: 'subscription.globalBanner.trialQrNotScanned.title',
        messageKey: 'subscription.globalBanner.trialQrNotScanned.message',
        actionKey: 'subscription.globalBanner.trialQrNotScanned.action',
        progressMessage: 'subscription.globalBanner.trialQrNotScanned.progressMessage',
      };
    case EOnboardingStage.COMPLETE:
      return {
        titleKey: 'subscription.globalBanner.trialComplete.title',
        messageKey: 'subscription.globalBanner.trialComplete.message',
        actionKey: 'subscription.globalBanner.trialComplete.action',
        progressMessage: 'subscription.globalBanner.trialComplete.progressMessage',
      };
    default:
      return null;
  }
};

export const getOnboardingStep = (step: EOnboardingStage) => {
  switch (step) {
    case EOnboardingStage.NO_QR:
      return 1;
    case EOnboardingStage.QR_NO_SCAN:
      return 2;
    case EOnboardingStage.COMPLETE:
      return 3;
    default:
      return 0;
  }
};
